import { Component, Input, OnInit, ViewEncapsulation, ElementRef, ViewChild, AfterViewInit, OnChanges, SimpleChanges, NgZone, ChangeDetectionStrategy } from '@angular/core';
import { register } from 'swiper/element/bundle';

export interface SlideItem {
  IMAGE: string;
  LINK?: string;
  ALT?: string;
  TITLE?: string;
  DESCRIPTION?: string;
}

// Register Swiper custom elements
register();

@Component({
    selector: 'app-image-slider',
    templateUrl: './image-slider.component.html',
    styleUrls: ['./image-slider.component.css'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ImageSliderComponent implements OnInit, AfterViewInit, OnChanges {
  @Input() slides: SlideItem[] = [];
  @Input() autoPlay: boolean = true;
  @Input() interval: number = 3000;
  @Input() showIndicators: boolean = true;
  @Input() showNavigationArrows: boolean = true;
  @Input() height: string = '400px';
  @Input() borderRadius: string = '8px';
  @Input() infiniteLoop: boolean = true;
  // Additional optional inputs used in templates
  @Input() showOverlay: boolean = false;
  @Input() theme: 'default' | 'minimal' = 'default';
  @Input() size: 'small' | 'medium' | 'large' | 'custom' = 'medium';
  @Input() pauseOnHover: boolean = false;
  @Input('customHeight') customHeight?: string;

  @ViewChild('swiper') swiperRef!: ElementRef;
  private hoverBound = false;
  private initialized = false;
  private lastSlidesSig: string = '';
  
  constructor(private zone: NgZone) {}
  
  ngOnInit(): void {
    // Resolve height from provided options if explicit height not set by consumer
    if (!this.height || this.height === '0' || this.height === '0px') {
      this.height = '400px';
    }
    if (this.customHeight) {
      this.height = this.customHeight;
    } else if (!this.customHeight && !this.height) {
      this.height = this.getHeightFromSize();
    } else if (!this.customHeight && this.size && this.height === '400px') {
      // If default height and size provided, use size
      this.height = this.getHeightFromSize();
    }
  }
  
  ngAfterViewInit(): void {
    // Initialize the Swiper element with params
    this.zone.runOutsideAngular(() => {
      setTimeout(() => {
        this.initializeSwiper();
        this.setupHoverPause();
      });
    });
  }
  
  ngOnChanges(changes: SimpleChanges): void {
    // Only react when slides really changed (not just new array ref)
    if (changes['slides'] && !changes['slides'].firstChange) {
      const sig = this.computeSlidesSig();
      if (sig !== this.lastSlidesSig) {
        this.lastSlidesSig = sig;
        this.zone.runOutsideAngular(() => {
          setTimeout(() => this.updateSwiper(), 0);
        });
      }
    }
    // For basic config toggles, avoid reassigning all params; just update
    if (changes['autoPlay'] || changes['interval'] || changes['showIndicators'] ||
        changes['showNavigationArrows'] || changes['infiniteLoop']) {
      this.zone.runOutsideAngular(() => {
        setTimeout(() => this.updateSwiper(), 0);
      });
    }
    if (changes['customHeight'] || changes['size']) {
      if (this.customHeight) {
        this.height = this.customHeight;
      } else {
        this.height = this.getHeightFromSize();
      }
    }
  }
  
  get autoplayConfig(): Record<string, any> | boolean {
    if (!this.autoPlay || this.slides.length <= 1) {
      return false;
    }
    return {
      delay: this.interval,
      disableOnInteraction: false,
    };
  }
  
  get paginationConfig(): any {
    return this.showIndicators && this.slides.length > 1 ? { clickable: true } : false;
  }
  
  get navigationConfig(): boolean {
    return this.showNavigationArrows && this.slides.length > 1;
  }
  
  get loopConfig(): boolean {
  // Require >= 3 slides for reliable loop; avoids Swiper warning and glitches
  return this.infiniteLoop && this.slides.length >= 3;
  }
  
  goToLink(slide: SlideItem, event: Event): void {
    if (slide.LINK && slide.LINK !== '#') {
      window.open(slide.LINK, '_blank', 'noopener,noreferrer');
      event.preventDefault();
      event.stopPropagation();
    }
  }

  private getHeightFromSize(): string {
    switch (this.size) {
      case 'small':
        return '140px';
      case 'large':
        return '560px';
      case 'custom':
        return this.customHeight || this.height || '400px';
      case 'medium':
      default:
        return '400px';
    }
  }

  private setupHoverPause(): void {
    if (!this.pauseOnHover || !this.autoPlay || this.hoverBound) return;
    const el: any = this.swiperRef?.nativeElement;
    if (!el) return;
    this.hoverBound = true;
    this.zone.runOutsideAngular(() => {
      el.addEventListener('mouseenter', () => {
        try { el.swiper?.autoplay?.stop?.(); } catch {}
      });
      el.addEventListener('mouseleave', () => {
        try { el.swiper?.autoplay?.start?.(); } catch {}
      });
    });
  }

  private updateSwiper(): void {
    const el: any = this.swiperRef?.nativeElement;
    if (el?.swiper) {
      try {
        el.swiper.update?.();
        if (this.autoplayConfig) {
          el.swiper.autoplay?.start?.();
        } else {
          el.swiper.autoplay?.stop?.();
        }
      } catch {}
    }
  }

  private buildParams() {
    return {
      loop: this.loopConfig,
      autoplay: this.autoplayConfig,
      pagination: this.paginationConfig,
      navigation: this.navigationConfig,
      slidesPerView: 1,
      spaceBetween: 0,
      centeredSlides: true,
      grabCursor: true,
      speed: 500,
      effect: 'slide',
      observer: true,
  observeParents: true,
  watchOverflow: true
    } as any;
  }

  private applyParams(): void {
    const el: any = this.swiperRef?.nativeElement;
    if (!el) return;
    try {
      Object.assign(el, this.buildParams());
    } catch {}
  }

  private initializeSwiper(): void {
    const el: any = this.swiperRef?.nativeElement;
    if (!el || this.initialized) return;
    this.applyParams();
    try {
      el.initialize?.();
      this.initialized = true;
      this.lastSlidesSig = this.computeSlidesSig();
    } catch {}
  }

  private computeSlidesSig(): string {
    try {
      return (this.slides || []).map(s => `${s.IMAGE}|${s.LINK || ''}|${s.ALT || ''}`).join(',');
    } catch {
      return `${this.slides?.length || 0}`;
    }
  }
}
